// Category-aware spec fields for the New SKU dialog. Single source of truth for:
//   - which spec fields each category captures (SPEC_FIELDS_BY_CATEGORY, keyed by the
//     category NAME — resolve the id from the loaded categories list, never hardcode ids)
//   - each field's label, input control, and payload value type (SPEC_FIELD_META)
//
// Field keys are the GENERIC schema column names (the same ones the server writes and
// resolveSkuAttributeFields reads) — do NOT introduce a parallel naming. partNo is NOT
// in this map: it's a universal field rendered for every category by the dialog.

export type SpecControl = 'select' | 'combobox' | 'numeric';
// 'number' → sent to the API as a number (numeric columns); 'string' → sent as text.
// thickness/flatCoilWidth render as numeric inputs but are TEXT columns → 'string'.
export type SpecValueType = 'string' | 'number';

export interface SpecFieldMeta {
  label: string;
  control: SpecControl;
  valueType: SpecValueType;
}

export const SPEC_FIELD_META = {
  // select — low-cardinality, clean
  rValue: { label: 'R-value', control: 'select', valueType: 'string' },
  face: { label: 'Face', control: 'select', valueType: 'string' },
  material: { label: 'Material', control: 'select', valueType: 'string' },
  coilProfile: { label: 'Coil profile', control: 'select', valueType: 'string' },
  gutterFaceSize: { label: 'Gutter face size', control: 'select', valueType: 'string' },
  coilTemper: { label: 'Temper', control: 'select', valueType: 'string' },
  style: { label: 'Style', control: 'select', valueType: 'string' },
  angle: { label: 'Angle', control: 'select', valueType: 'string' },
  // combobox — type-or-pick (accepts new values)
  color: { label: 'Color', control: 'combobox', valueType: 'string' },
  typeAttr: { label: 'Type', control: 'combobox', valueType: 'string' },
  size: { label: 'Size', control: 'combobox', valueType: 'string' },
  brand: { label: 'Brand', control: 'combobox', valueType: 'string' },
  widthAttr: { label: 'Width', control: 'combobox', valueType: 'string' },
  lengthAttr: { label: 'Length', control: 'combobox', valueType: 'string' },
  // numeric inputs — dimensional. widthIn/lengthIn/coverageSf/lbsPerLf are numeric
  // columns ('number'); thickness/flatCoilWidth are TEXT columns ('string').
  thickness: { label: 'Thickness', control: 'numeric', valueType: 'string' },
  flatCoilWidth: { label: 'Flat coil width', control: 'numeric', valueType: 'string' },
  lbsPerLf: { label: 'Lbs/LF', control: 'numeric', valueType: 'number' },
  widthIn: { label: 'Width in', control: 'numeric', valueType: 'number' },
  lengthIn: { label: 'Length in', control: 'numeric', valueType: 'number' },
  coverageSf: { label: 'Coverage SF', control: 'numeric', valueType: 'number' },
} as const satisfies Record<string, SpecFieldMeta>;

export type SpecFieldKey = keyof typeof SPEC_FIELD_META;

// Per-category spec fields, keyed by category NAME (exact strings from product-line.ts).
// Verbatim from the curated map. Categories not listed render only the universal Part #.
export const SPEC_FIELDS_BY_CATEGORY: Record<string, SpecFieldKey[]> = {
  Insulation: ['rValue', 'face', 'widthIn', 'lengthIn', 'coverageSf'],
  'Gutter Coil': ['material', 'color', 'thickness', 'flatCoilWidth', 'lbsPerLf', 'coilProfile', 'gutterFaceSize', 'coilTemper'],
  Downspouts: ['style', 'material', 'size', 'color', 'lengthAttr'],
  Elbows: ['style', 'material', 'size', 'color', 'angle', 'typeAttr'],
  Miters: ['style', 'material', 'size', 'color', 'typeAttr'],
  'End Caps': ['style', 'material', 'size', 'color'],
  'Hangers & Pipe Bands': ['typeAttr', 'material', 'size', 'style'],
  'Gutter Guards': ['material', 'widthAttr', 'lengthAttr', 'typeAttr'],
  'Sealer & Paint': ['typeAttr', 'size', 'color'],
  'Adapters & Conductors': ['typeAttr', 'brand', 'color'],
  Staples: ['typeAttr', 'lengthAttr'],
  'Tools & Equipment': ['typeAttr', 'size'],
  Fasteners: ['typeAttr'],
  'Bolts & Anchors': ['typeAttr'],
  Outlets: ['typeAttr'],
  'Safety/PPE': ['typeAttr'],
  'Wedges & Shanks': ['typeAttr'],
  'Insulation Accessories': ['typeAttr'],
};

/** Spec fields for a category by name; [] when the category has no spec map (the
 *  dialog still renders the universal Part # field). */
export function specFieldsForCategory(categoryName: string | undefined | null): SpecFieldKey[] {
  if (!categoryName) return [];
  return SPEC_FIELDS_BY_CATEGORY[categoryName.trim()] ?? [];
}
